// ClientGallery.js
// A "Client Gallery" button that opens a pop-up window.
// Regular clients: type their gallery code and their Dropbox folder opens.
// School events: type the event code, then their ticket number, and only
// the small folder with their family's photos opens (never the whole event).

import { useState, useEffect } from "react";
import "./ClientGallery.css"; // Import the CSS file for styling


// ------------------------------------------------------------
// SETTINGS: the only part I need to edit
// ------------------------------------------------------------

// My email for the "Need help?" link
const HELP_EMAIL = "hello@westlaportraits.com";

// Halloween look (pumpkins + orange). Set to false after the event
// to go back to my regular black-and-white style.
const HALLOWEEN_THEME = true;

// 1. My regular clients (one code = one Dropbox folder link).
//    Write codes in CAPITAL letters. Clients can type them any way.
const GALLERIES = {
  "SMITH-FALL26": "",
  "GARCIA-0921": "",
};

// 2. My school events (one code for all parents, split by ticket numbers).
//    Each line is one subfolder in Dropbox, shared on its own.
//    from / to = the ticket numbers inside that subfolder.
//    Leave link as "" until I paste the real Dropbox link. Parents will
//    see "not ready yet" instead of an error.
const EVENTS = {
  "MARVISTA2026": [
    { from: 1,   to: 25,  link: "https://www.dropbox.com/scl/fo/tme4kln7c47mkbeuk8x5o/AIWgZNzgEaHD9lFHeu-kpE8?rlkey=b0rfz26mvzlkm2fv2nqis3sqg&st=imqst14s&dl=0" }, // 001-025
    { from: 26,  to: 50,  link: "" }, // 026-050
    { from: 51,  to: 75,  link: "" }, // 051-075
    { from: 76,  to: 100, link: "" }, // 076-100
    { from: 101, to: 125, link: "" }, // 101-125
    { from: 126, to: 150, link: "" }, // 126-150
    { from: 151, to: 175, link: "" }, // 151-175
    { from: 176, to: 200, link: "" }, // 176-200
  ],
};

// All the messages people can see, in one place so they're easy to change
const MESSAGES = {
  badCode: "That code didn't match a gallery. Please check your email or contact me.",
  badTicket: "We couldn't find that ticket number. Please check your ticket card.",
  notReady: "Your photos aren't ready yet. Please check back soon.",
};


// ------------------------------------------------------------
// HELPERS: small functions that do one job each
// ------------------------------------------------------------

// Clean up what someone typed: " Mar Vista 2026 " -> "MARVISTA2026"
// (removes ALL spaces, even ones in the middle that phones add)
function cleanUpCode(text) {
  return text.replace(/\s/g, "").toUpperCase();
}

// Turn "#087" or " 87 " into the number 87 (or NaN if there are no digits)
function cleanUpTicket(text) {
  return parseInt(text.replace(/\D/g, ""), 10);
}

// Find the ticket group (subfolder) that holds this ticket number
function findGroup(eventCode, ticketNumber) {
  return EVENTS[eventCode].find(
    (group) => ticketNumber >= group.from && ticketNumber <= group.to
  );
}

// Open a Dropbox link in a new tab
function openInNewTab(link) {
  window.open(link, "_blank", "noopener,noreferrer");
}


// ------------------------------------------------------------
// THE COMPONENT
// ------------------------------------------------------------

export default function ClientGallery() {
  // Extra CSS class that switches on the Halloween colors
  const theme = HALLOWEEN_THEME ? " cg-halloween" : "";

  // isOpen tracks whether the pop-up window is showing
  const [isOpen, setIsOpen] = useState(false);
  // code holds what the client types in the first box
  const [code, setCode] = useState("");
  // ticket holds the ticket number (only used for school events)
  const [ticket, setTicket] = useState("");
  // eventCode remembers the event once the code is correct (step 2)
  const [eventCode, setEventCode] = useState(null);
  // error holds a message if something is wrong
  const [error, setError] = useState("");


  // Reset everything and close the pop-up
  function closeWindow() {
    setIsOpen(false);
    setCode("");
    setTicket("");
    setEventCode(null);
    setError("");
  }


  // Let the client close the pop-up with the Escape key
  // (only listen while the pop-up is open)
  useEffect(() => {
    if (!isOpen) return;

    const handleKey = (e) => {
      if (e.key === "Escape") closeWindow();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [isOpen]);


  // Open a gallery link, or say "not ready yet" if I haven't added it
  function openGallery(link) {
    if (!link) {
      setError(MESSAGES.notReady);
      return;
    }
    openInNewTab(link);
    closeWindow();
  }


  // Step 1: check the gallery code
  function handleCodeSubmit(e) {
    e.preventDefault(); // stop the page from reloading

    const cleanCode = cleanUpCode(code);

    if (cleanCode in GALLERIES) {
      openGallery(GALLERIES[cleanCode]); // regular client: open right away
    } else if (cleanCode in EVENTS) {
      setEventCode(cleanCode); // school event: ask for the ticket next
      setError("");
    } else {
      setError(MESSAGES.badCode);
    }
  }


  // Step 2 (events only): find the subfolder for the ticket number
  function handleTicketSubmit(e) {
    e.preventDefault();

    const group = findGroup(eventCode, cleanUpTicket(ticket));

    if (group) {
      openGallery(group.link);
    } else {
      setError(MESSAGES.badTicket);
    }
  }


  // Clear the error as soon as someone starts typing again
  function handleTyping(setValue) {
    return (e) => {
      setValue(e.target.value);
      setError("");
    };
  }


  return (
    <>
      {/* The button that shows up on my site */}
      <button type="button" className={"cg-open-btn" + theme} onClick={() => setIsOpen(true)}>
        Client Gallery
      </button>

      {/* The pop-up window, only shown when isOpen is true */}
      {isOpen && (
        <div className={"cg-overlay" + theme} onClick={closeWindow}>
          {/* stopPropagation keeps clicks inside the box from closing it */}
          <div
            className="cg-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cg-title"
            onClick={(e) => e.stopPropagation()}
          >
            <button type="button" className="cg-close" onClick={closeWindow} aria-label="Close">
              ×
            </button>

            <h2 id="cg-title">Client Gallery</h2>

            {/* Step 1: gallery code */}
            {!eventCode && (
              <>
                <p>Enter the gallery code from your email to view and download your photos.</p>
                <form onSubmit={handleCodeSubmit}>
                  <input
                    type="text"
                    value={code}
                    onChange={handleTyping(setCode)}
                    placeholder="Gallery code"
                    autoFocus
                    aria-label="Gallery code"
                  />
                  <button type="submit" className="cg-submit">
                    Continue
                  </button>
                </form>
              </>
            )}

            {/* Step 2: ticket number (school events only) */}
            {eventCode && (
              <>
                <p>Enter the number on your family's ticket card.</p>
                <form onSubmit={handleTicketSubmit}>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={ticket}
                    onChange={handleTyping(setTicket)}
                    placeholder="Ticket number"
                    autoFocus
                    aria-label="Ticket number"
                  />
                  <button type="submit" className="cg-submit">
                    View my photos
                  </button>
                </form>
              </>
            )}

            {/* role="alert" makes screen readers read the message out loud */}
            {error && <p className="cg-error" role="alert">{error}</p>}

            <p className="cg-help">
              Need help? <a href={`mailto:${HELP_EMAIL}`}>Email me</a>
            </p>
          </div>
        </div>
      )}
    </>
  );
}
